/**
 * Slug rewriting for portal and www routes.
 *
 * All route metadata is defined in co-located .meta.ts files next to each route.
 *
 * Used by:
 * - Router rewrites (input: external slug → internal route, output: internal route → locale slug)
 * - Language switcher (translate current slug to target locale)
 * - SEO components (canonical URL and hreflang alternates)
 * - Sitemaps (iterate routes to generate URLs with metadata)
 */
// --- Portal meta files (co-located with each route) -----------------------
import * as portalPrivacyPolicy from '@/routes/$portalId/privacy-policy.meta';
import * as portalLegalDisclaimer from '@/routes/$portalId/legal-disclaimer.meta';

// --- WWW meta files (co-located with each route) --------------------------
import * as wwwIndex from '@/routes/www/index.meta';
import * as aboutUs from '@/routes/www/about-us.meta';
import * as contact from '@/routes/www/contact.meta';
import * as roundUp from '@/routes/www/round-up.meta';
import * as transparency from '@/routes/www/transparency.meta';
import * as liveCounter from '@/routes/www/live-counter.meta';
import * as privacyPolicy from '@/routes/www/privacy-policy.meta';
import * as legalDisclaimer from '@/routes/www/legal-disclaimer.meta';
import * as cookies from '@/routes/www/cookies.meta';
import * as selectionCriteria from '@/routes/www/selection-criteria.meta';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RouteSlugEntry {
	routeName: string;
	slugs: Record<string, string>;
	/** Sitemap metadata — pages without this field are excluded from the sitemap */
	sitemap?: { changefreq: string; priority: string; lastmod: string };
}

export interface SlugMaps {
	/** External slug (any locale) → internal route segment */
	slugToRoute: Record<string, string>;
	/** Internal route segment → { locale: slug } */
	routeToSlugs: Record<string, Record<string, string>>;
}

// ---------------------------------------------------------------------------
// Map builder
// ---------------------------------------------------------------------------

function buildMaps(routes: RouteSlugEntry[]): SlugMaps {
	const slugToRoute: Record<string, string> = {};
	const routeToSlugs: Record<string, Record<string, string>> = {};

	for (const { routeName, slugs } of routes) {
		routeToSlugs[routeName] = slugs;
		for (const locale in slugs) {
			if (!Object.hasOwn(slugs, locale)) continue;
			slugToRoute[slugs[locale]!] = routeName;
		}
	}

	return { slugToRoute, routeToSlugs };
}

// ---------------------------------------------------------------------------
// Portal routes — add new entries here when creating a portal page with slugs
// ---------------------------------------------------------------------------

export const portalRoutes: RouteSlugEntry[] = [portalPrivacyPolicy, portalLegalDisclaimer];
export const portalSlugs = buildMaps(portalRoutes);

// ---------------------------------------------------------------------------
// WWW routes — add new entries here when creating a www page with slugs
// ---------------------------------------------------------------------------

export const wwwRoutes: RouteSlugEntry[] = [
	wwwIndex,
	aboutUs,
	contact,
	roundUp,
	transparency,
	liveCounter,
	privacyPolicy,
	legalDisclaimer,
	cookies,
	selectionCriteria,
];

export const wwwSlugs = buildMaps(wwwRoutes);

// ---------------------------------------------------------------------------
// Language switcher helper
// ---------------------------------------------------------------------------

/** Reverse map: any slug (any locale, any context) → its locale slugs */
const allSlugToSlugs: Record<string, Record<string, string>> = {};
for (const maps of [portalSlugs, wwwSlugs]) {
	for (const route in maps.routeToSlugs) {
		if (!Object.hasOwn(maps.routeToSlugs, route)) continue;
		const slugsByLocale = maps.routeToSlugs[route]!;
		for (const locale in slugsByLocale) {
			if (!Object.hasOwn(slugsByLocale, locale)) continue;
			allSlugToSlugs[slugsByLocale[locale]!] = slugsByLocale;
		}
	}
}

/**
 * Translate a page slug to its equivalent in another locale.
 * Works for both portal and www pages.
 * Returns undefined when the slug is not recognized.
 */
export function translateSlug(slug: string, targetLocale: string): string | undefined {
	return allSlugToSlugs[slug]?.[targetLocale];
}
